import { useState, useRef, useCallback, useMemo } from 'react';
import {
  Upload,
  File,
  FileText,
  Image,
  FileSpreadsheet,
  FileArchive,
  Film,
  Music,
  X,
  Download,
  Eye,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Badge } from '@centraoneplus/ui';
import { fileService } from '@services/file.service';
import { ATTACHMENT_ACCEPT, isFileTypeAccepted } from './attachmentPolicy';

export interface Attachment {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  url: string;
  thumbnailUrl?: string;
  uploadedBy?: string;
  uploadedAt: string;
  // When false, this attachment can't be removed (e.g. a file that arrived on a
  // customer's inbound email — it isn't an independent file record we own). The
  // delete button is hidden for these. Undefined/true means deletable.
  deletable?: boolean;
}

interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

// File type icons
const FILE_ICONS: Record<string, React.ElementType> = {
  'image': Image,
  'video': Film,
  'audio': Music,
  'application/pdf': FileText,
  'application/msword': FileText,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': FileText,
  'application/vnd.ms-excel': FileSpreadsheet,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileSpreadsheet,
  'application/zip': FileArchive,
  'application/x-rar-compressed': FileArchive,
  'default': File,
};

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// Get file icon based on mime type
function getFileIcon(mimeType: string): React.ElementType {
  if (mimeType.startsWith('image/')) return FILE_ICONS['image'];
  if (mimeType.startsWith('video/')) return FILE_ICONS['video'];
  if (mimeType.startsWith('audio/')) return FILE_ICONS['audio'];
  return FILE_ICONS[mimeType] || FILE_ICONS['default'];
}

// Get file extension color
function getExtensionColor(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300';
  if (mimeType.startsWith('video/')) return 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300';
  if (mimeType.startsWith('audio/')) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  if (mimeType.includes('pdf')) return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  if (mimeType.includes('word')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  if (mimeType.includes('zip') || mimeType.includes('rar')) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200';
}

interface FileUploadZoneProps {
  onUpload: (files: File[]) => void;
  accept?: string;
  maxFiles?: number;
  maxSize?: number; // in bytes
  disabled?: boolean;
}

export function FileUploadZone({
  onUpload,
  accept = '*/*',
  maxFiles,
  maxSize = 10 * 1024 * 1024, // 10MB default
  disabled = false,
}: FileUploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFiles = useCallback((files: File[]): File[] => {
    setError(null);

    const errors: string[] = [];
    let candidates = files;

    if (maxFiles && candidates.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed`);
      candidates = candidates.slice(0, maxFiles);
    }

    const validFiles = candidates.filter((file) => {
      // Enforce the accept whitelist in app code — the browser only applies it to
      // the file-picker dialog, so drag-drop / programmatic adds slip past it.
      if (!isFileTypeAccepted(file, accept)) {
        errors.push(`File "${file.name}" type is not allowed`);
        return false;
      }
      if (file.size > maxSize) {
        errors.push(`File "${file.name}" exceeds ${formatFileSize(maxSize)} limit`);
        return false;
      }
      return true;
    });

    if (errors.length > 0) {
      setError(errors.join('. '));
    }

    return validFiles;
  }, [accept, maxFiles, maxSize]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const validFiles = validateFiles(files);
    if (validFiles.length > 0) {
      onUpload(validFiles);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        onUpload(validFiles);
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragging
            ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-500 dark:hover:bg-gray-800'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragging ? 'text-primary-500' : 'text-gray-400'} dark:text-gray-500`} />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium text-primary-600">Click to upload</span> or drag and drop
        </p>
        <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
          Max {formatFileSize(maxSize)} per file
          {maxFiles ? `, up to ${maxFiles} files` : ''}
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}

interface UploadingFileItemProps {
  file: UploadingFile;
  onCancel: () => void;
}

function UploadingFileItem({ file, onCancel }: UploadingFileItemProps) {
  const FileIcon = getFileIcon(file.file.type);

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg dark:bg-gray-900">
      <div className={`p-2 rounded ${getExtensionColor(file.file.type)}`}>
        <FileIcon className="h-4 w-4" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">{file.file.name}</p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden dark:bg-gray-700">
            <div
              className={`h-full transition-all ${
                file.status === 'error' ? 'bg-red-500' : 'bg-primary-500'
              }`}
              style={{ width: `${file.progress}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {file.status === 'uploading' && `${file.progress}%`}
            {file.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
            {file.status === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
          </span>
        </div>
        {file.error && <p className="text-xs text-red-500 mt-1">{file.error}</p>}
      </div>

      {file.status === 'uploading' && (
        <button onClick={onCancel} className="p-1 text-gray-400 hover:text-red-500 dark:text-gray-500">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

interface AttachmentItemProps {
  attachment: Attachment;
  onDelete?: (id: string) => void;
  onPreview?: (attachment: Attachment) => void;
  showActions?: boolean;
}

function AttachmentItem({
  attachment,
  onDelete,
  onPreview,
  showActions = true,
}: AttachmentItemProps) {
  const FileIcon = getFileIcon(attachment.mimeType);
  const isImage = attachment.mimeType.startsWith('image/');

  return (
    <div className="group flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors dark:bg-gray-800 dark:border-gray-700">
      {/* Thumbnail or Icon */}
      {isImage && attachment.thumbnailUrl ? (
        <div className="h-10 w-10 rounded overflow-hidden bg-gray-100 flex-shrink-0 dark:bg-gray-800">
          <img
            src={attachment.thumbnailUrl}
            alt={attachment.name}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className={`p-2 rounded ${getExtensionColor(attachment.mimeType)} flex-shrink-0`}>
          <FileIcon className="h-4 w-4" />
        </div>
      )}

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">{attachment.name}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatFileSize(attachment.size)}
          {attachment.uploadedAt && (
            <span> &middot; {new Date(attachment.uploadedAt).toLocaleDateString()}</span>
          )}
        </p>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {isImage && onPreview && (
            <button
              onClick={() => onPreview(attachment)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700"
              title="Preview"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => fileService.downloadAttachment(attachment)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700"
            title="Download"
          >
            <Download className="h-4 w-4" />
          </button>
          {onDelete && attachment.deletable !== false && (
            <button
              onClick={() => onDelete(attachment.id)}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded dark:text-gray-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface AttachmentListProps {
  attachments: Attachment[];
  onDelete?: (id: string) => void;
  onPreview?: (attachment: Attachment) => void;
  emptyMessage?: string;
}

// How many attachments to show before the "show more" control, and how many
// more to reveal per click.
const ATTACHMENT_PAGE_SIZE = 10;

export function AttachmentList({
  attachments,
  onDelete,
  onPreview,
  emptyMessage = 'No attachments',
}: AttachmentListProps) {
  const [visibleCount, setVisibleCount] = useState(ATTACHMENT_PAGE_SIZE);

  if (attachments.length === 0) {
    return (
      <div className="text-center py-6 text-gray-400 dark:text-gray-500">
        <File className="h-8 w-8 mx-auto mb-2" />
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  const visible = attachments.slice(0, visibleCount);
  const remaining = attachments.length - visible.length;
  const isExpanded = visibleCount > ATTACHMENT_PAGE_SIZE;

  return (
    <div className="space-y-2">
      {visible.map((attachment) => (
        <AttachmentItem
          key={attachment.id}
          attachment={attachment}
          onDelete={onDelete}
          onPreview={onPreview}
        />
      ))}

      {remaining > 0 && (
        <button
          type="button"
          onClick={() =>
            setVisibleCount((c) =>
              Math.min(c + ATTACHMENT_PAGE_SIZE, attachments.length)
            )
          }
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
        >
          <ChevronDown className="h-4 w-4" />
          Show {remaining} more
        </button>
      )}

      {remaining === 0 && isExpanded && (
        <button
          type="button"
          onClick={() => setVisibleCount(ATTACHMENT_PAGE_SIZE)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
        >
          <ChevronUp className="h-4 w-4" />
          Show less
        </button>
      )}
    </div>
  );
}

interface FileAttachmentsProps {
  attachments: Attachment[];
  onUpload: (files: File[]) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onPreview?: (attachment: Attachment) => void;
  isUploading?: boolean;
  accept?: string;
  maxFiles?: number;
  maxSize?: number;
  disabled?: boolean;
  showUpload?: boolean;
}

// Once a batch has more than this many files in flight, the per-file progress
// rows collapse into a single aggregate summary so the panel doesn't grow
// unbounded (e.g. uploading 50 files at once). Users can still expand to see
// every row.
const UPLOAD_SUMMARY_THRESHOLD = 8;

export function FileAttachments({
  attachments,
  onUpload,
  onDelete,
  onPreview,
  isUploading = false,
  accept = ATTACHMENT_ACCEPT,
  maxFiles,
  maxSize = 10 * 1024 * 1024,
  disabled = false,
  showUpload = true,
}: FileAttachmentsProps) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [showAllUploading, setShowAllUploading] = useState(false);

  // Aggregate progress for the collapsed summary line.
  const uploadSummary = useMemo(() => {
    const total = uploadingFiles.length;
    const completed = uploadingFiles.filter((f) => f.status === 'completed').length;
    const errored = uploadingFiles.filter((f) => f.status === 'error').length;
    const overallProgress =
      total === 0
        ? 0
        : Math.round(uploadingFiles.reduce((sum, f) => sum + f.progress, 0) / total);
    return { total, completed, errored, overallProgress };
  }, [uploadingFiles]);

  const collapseUploads =
    uploadingFiles.length > UPLOAD_SUMMARY_THRESHOLD && !showAllUploading;

  const handleUpload = async (files: File[]) => {
    // Add files to uploading state with simulated progress
    const newUploadingFiles: UploadingFile[] = files.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      progress: 0,
      status: 'uploading' as const,
    }));

    setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

    // Simulate progress
    const progressIntervals = newUploadingFiles.map((uploadFile) => {
      return setInterval(() => {
        setUploadingFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id && f.progress < 90
              ? { ...f, progress: f.progress + 10 }
              : f
          )
        );
      }, 200);
    });

    try {
      await onUpload(files);

      // Mark as completed
      setUploadingFiles((prev) =>
        prev.map((f) =>
          newUploadingFiles.find((nf) => nf.id === f.id)
            ? { ...f, progress: 100, status: 'completed' as const }
            : f
        )
      );

      // Clear completed files after delay
      setTimeout(() => {
        setUploadingFiles((prev) =>
          prev.filter((f) => !newUploadingFiles.find((nf) => nf.id === f.id))
        );
      }, 2000);
    } catch (error) {
      setUploadingFiles((prev) =>
        prev.map((f) =>
          newUploadingFiles.find((nf) => nf.id === f.id)
            ? { ...f, status: 'error' as const, error: 'Upload failed' }
            : f
        )
      );
    } finally {
      progressIntervals.forEach((interval) => clearInterval(interval));
    }
  };

  const cancelUpload = (id: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">
          Attachments
          {attachments.length > 0 && (
            <Badge variant="default" size="sm" className="ml-2">
              {attachments.length}
            </Badge>
          )}
        </h4>
        {isUploading && (
          <Loader2 className="h-4 w-4 text-primary-500 animate-spin" />
        )}
      </div>

      {/* Upload Zone */}
      {showUpload && (
        <FileUploadZone
          onUpload={handleUpload}
          accept={accept}
          maxFiles={maxFiles}
          maxSize={maxSize}
          disabled={disabled || isUploading}
        />
      )}

      {/* Uploading Files */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {/* Aggregate summary — shown for large batches, expandable to per-file rows. */}
          {uploadingFiles.length > UPLOAD_SUMMARY_THRESHOLD && (
            <button
              type="button"
              onClick={() => setShowAllUploading((v) => !v)}
              className="w-full rounded-lg border border-gray-200 p-3 text-left hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {uploadSummary.completed < uploadSummary.total
                    ? `Uploading ${uploadSummary.completed} of ${uploadSummary.total} files…`
                    : `Uploaded ${uploadSummary.total} files`}
                  {uploadSummary.errored > 0 && (
                    <span className="text-red-500"> &middot; {uploadSummary.errored} failed</span>
                  )}
                </span>
                {showAllUploading ? (
                  <ChevronUp className="h-4 w-4 flex-shrink-0 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                )}
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full bg-primary-500 transition-all"
                  style={{ width: `${uploadSummary.overallProgress}%` }}
                />
              </div>
            </button>
          )}

          {/* Per-file rows — hidden while the summary is collapsed; always capped
              with an internal scroll so the panel height stays bounded. */}
          {!collapseUploads && (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {uploadingFiles.map((file) => (
                <UploadingFileItem
                  key={file.id}
                  file={file}
                  onCancel={() => cancelUpload(file.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Attached Files */}
      <AttachmentList
        attachments={attachments}
        onDelete={onDelete}
        onPreview={onPreview}
      />
    </div>
  );
}
